import { Container, Matrix } from 'pixi.js';
import { CONFIG } from '../domain/config.js';

export const DIRECTIONS = ['south', 'east', 'north', 'west'] as const;
export type Direction = typeof DIRECTIONS[number];

/** Screen pixels per world unit along a floor axis. The floor is a 2:1 isometric diamond, like YAGE's
 *  isometric maps ((x - y), (x + y) / 2), scaled so the whole terminal stays close to its old footprint. */
export const ISO = Math.SQRT1_2;

let quarter = 0;

export function cameraQuarter() { return quarter; }
export function cameraDirection(): Direction { return DIRECTIONS[quarter]; }
export function rotateCamera(steps: number) { quarter = (quarter + steps + 4) % 4; }

export function cameraAngle() { return -quarter * Math.PI / 2; }

/** World offset from the map centre, turned by the camera quarter. */
function turn(dx: number, dy: number) {
    if (quarter === 1) return { x: dy, y: -dx };
    if (quarter === 2) return { x: -dx, y: -dy };
    if (quarter === 3) return { x: -dy, y: dx };
    return { x: dx, y: dy };
}

function unturn(dx: number, dy: number) {
    if (quarter === 1) return { x: -dy, y: dx };
    if (quarter === 2) return { x: -dx, y: -dy };
    if (quarter === 3) return { x: dy, y: -dx };
    return { x: dx, y: dy };
}

/** World floor point to isometric screen space (the viewport's coordinates). */
export function projectWorld(x: number, y: number) {
    const r = turn(x - CONFIG.width / 2, y - CONFIG.height / 2);
    return { x: CONFIG.width / 2 + (r.x - r.y) * ISO, y: CONFIG.height / 2 + (r.x + r.y) * ISO / 2 };
}

/** Isometric screen point back to the world floor point under it. */
export function unprojectWorld(x: number, y: number) {
    const u = (x - CONFIG.width / 2) / ISO, v = (y - CONFIG.height / 2) * 2 / ISO;
    const r = unturn((u + v) / 2, (v - u) / 2);
    return { x: CONFIG.width / 2 + r.x, y: CONFIG.height / 2 + r.y };
}

/** Screen direction of a world vector, for picking sprite facings. */
export function projectDirection(dx: number, dy: number) {
    const r = turn(dx, dy);
    return { x: (r.x - r.y) * ISO, y: (r.x + r.y) * ISO / 2 };
}

/** Affine world-to-screen transform, for flat floor art drawn in world coordinates. */
export function floorMatrix() {
    const o = projectWorld(0, 0), ex = projectWorld(1, 0), ey = projectWorld(0, 1);
    return new Matrix(ex.x - o.x, ex.y - o.y, ey.x - o.x, ey.y - o.y, o.x, o.y);
}

/** Screen-space bounding box of a world rectangle's projected floor. */
export function projectedBounds(left: number, top: number, right: number, bottom: number) {
    const corners = [projectWorld(left, top), projectWorld(right, top), projectWorld(right, bottom), projectWorld(left, bottom)];
    const xs = corners.map(p => p.x), ys = corners.map(p => p.y);
    return { left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys) };
}

/** Stands an upright node on a world floor point, depth-sorted by its projected foot. */
export function standAt(node: Container, x: number, y: number, depth = 0) {
    const p = projectWorld(x, y);
    node.position.set(p.x, p.y);
    node.zIndex = p.y + depth;
}

/** TX-017 follow: the view holds still while the target stays inside a centred dead zone (half extents halfX, halfY). */
export function deadZoneCenter(center: number, target: number, half: number): number {
    return target > center + half ? target - half : target < center - half ? target + half : center;
}
